/**
 * RemoteInCall — the iPad-landscape in-call controller. Faithful to the design's
 * "Presence" in-call screen: status top-bar, feature row, layout segments,
 * moderator-gated HOST CONTROLS, a live participant roster pane, and the bottom
 * primary bar — plus the More sheet and the recording-consent modal.
 *
 * Controls that map to a real command are wired through callbacks (which call
 * RemoteApp's `send`); design surfaces with no command yet are visibly marked
 * `is-soon` (dimmed, non-interactive) rather than looking active-but-broken.
 *
 * The roster shows the REAL Jitsi meeting participants the TV relays in presence
 * (incl. the TV itself by its meeting display name). The Remote is NOT a Jitsi
 * participant, so it never appears here.
 */

import { useState } from 'react';
import type { ChatMessage, MeetingParticipant } from '@spaces/contracts';
import {
    Board, Captions, Chat, Check, Hand, Hangup, Info, Keypad, Lock, Mic, MicOff, Monitor,
    More, Pin, Record, ScreenShare, SpeakerView, Tile, Users, Video, VideoOff, X
} from '../ui/icons.js';
import { Avatar, Dot, Pill } from '../ui/components.js';

export interface RemoteInCallProps {
    roomName: string;
    meetingName: string;
    /** Jitsi meeting domain (config), used to build the join link in Meeting info. */
    meetingDomain: string;
    /** The pickup host (config, e.g. "cast.8x8.vc") shown in Meeting info. */
    shareHost: string;
    /** The rotating share key this Remote paired with — shown in Meeting info. */
    shareKey: string;
    tvConnected: boolean;
    isModerator: boolean;
    audioMuted: boolean;
    videoMuted: boolean;
    tileView: boolean;
    recording: boolean;
    screensharing: boolean;
    handRaised: boolean;
    /** The TV's meeting is locked and waiting for a password (Jitsi `passwordRequired`). */
    needPassword: boolean;
    /** REAL Jitsi meeting participants as the TV sees them (incl. the TV itself). */
    participants: MeetingParticipant[];
    /** Whether the room's chat panel is currently open on the TV (drives the toggle). */
    chatPanelOpen: boolean;
    /** Recent chat thread (oldest → newest) the TV relays so the controller can read it. */
    chatThread: ChatMessage[];
    lastAck: string;
    onMute: () => void;
    onVideo: () => void;
    onLayout: (tile: boolean) => void;
    onRecord: (on: boolean) => void;
    onRaiseHand: (raised: boolean) => void;
    onWhiteboard: () => void;
    onShare: () => void;
    /** Send a chat message into the meeting (read on the TV; any participant may chat). */
    onChat: (text: string) => void;
    /** Open or close the room's chat panel on the TV, independent of sending a message. */
    onSetChatOpen: (open: boolean) => void;
    /** Show or hide the meeting's captions pane on the TV. */
    onCaptions: (on: boolean) => void;
    /** Pin a participant to the TV's main stage (empty id unpins everyone). */
    onPin: (participantId: string) => void;
    /** Moderator: mute everyone else in the meeting. */
    onMuteAll: () => void;
    /** Moderator: enable or disable the meeting lobby. */
    onLobby: (enabled: boolean) => void;
    /** Send a single DTMF tone (dial-pad) into the meeting. */
    onTone: (digit: string) => void;
    /** Submit the password for a locked meeting. */
    onSubmitPassword: (password: string) => void;
    onHangup: () => void;
}

/** A real Jitsi meeting participant rendered as a roster row. */
function ParticipantRow({ p }: { p: MeetingParticipant }): JSX.Element {
    const name = (p.displayName ?? '').trim() || (p.local ? 'Room TV' : 'Guest');
    const isMod = p.role === 'moderator';
    const sub = p.local ? 'Room TV · in the meeting' : isMod ? 'Host' : 'Participant';

    return (
        <div className = { `occ${p.local ? ' occ--self' : ''}` }>
            <Avatar label = { name } seed = { p.participantId } />
            <div style = {{ minWidth: 0 }}>
                <div className = "occ__name">{ name }</div>
                <div className = "occ__sub">{ sub }</div>
            </div>
            <div className = "occ__icons">
                { p.local ? <Monitor /> : null }
                { isMod ? <Pill label = "HOST" variant = "host" /> : null }
            </div>
        </div>
    );
}

function MoreSheet(props: {
    onClose: () => void;
    onShare: () => void;
    onWhiteboard: () => void;
    onRaiseHand: () => void;
    onDialpad: () => void;
    captionsOn: boolean;
    onCaptions: () => void;
    onMeetingInfo: () => void;
}): JSX.Element {
    return (
        <div className = "scrim" onClick = { props.onClose }>
            <div className = "modal" style = {{ maxWidth: '46cqw', textAlign: 'left' }} onClick = { e => e.stopPropagation() }>
                <div className = "row" style = {{ justifyContent: 'space-between', marginBottom: '1.2cqw' }}>
                    <span className = "modal__title" style = {{ margin: 0 }}>More</span>
                    <span className = "linkbtn" onClick = { props.onClose }><X /></span>
                </div>
                <div className = "sheet-grid">
                    <button className = "sheet-item" onClick = { () => { props.onShare(); props.onClose(); } } data-testid = "rm-sheet-share">
                        <ScreenShare /> Share content
                    </button>
                    <button className = "sheet-item" onClick = { () => { props.onWhiteboard(); props.onClose(); } } data-testid = "rm-sheet-whiteboard">
                        <Board /> Whiteboard
                    </button>
                    <button className = "sheet-item" onClick = { () => { props.onRaiseHand(); props.onClose(); } } data-testid = "rm-sheet-raise">
                        <Hand /> Raise hand
                    </button>
                    <button className = "sheet-item" onClick = { () => { props.onDialpad(); props.onClose(); } } data-testid = "rm-sheet-dialpad">
                        <Keypad /> Dial pad
                    </button>
                    <button
                        className = { `sheet-item${props.captionsOn ? ' sheet-item--on' : ''}` }
                        onClick = { props.onCaptions }
                        data-testid = "rm-sheet-captions">
                        <Captions /> Live captions{ props.captionsOn ? ' · On' : '' }
                    </button>
                    <button className = "sheet-item" onClick = { () => { props.onMeetingInfo(); props.onClose(); } } data-testid = "rm-sheet-info">
                        <Info /> Meeting info
                    </button>
                </div>
            </div>
        </div>
    );
}

/** DTMF dial-pad — sends touch tones into the meeting for IVRs / dial-in bridges. */
function DialPad(props: { onClose: () => void; onTone: (digit: string) => void }): JSX.Element {
    const [entered, setEntered] = useState<string>('');
    const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

    const press = (digit: string): void => {
        setEntered(prev => (prev + digit).slice(-24));
        props.onTone(digit);
    };

    return (
        <div className = "scrim" onClick = { props.onClose }>
            <div className = "modal" style = {{ maxWidth: '34cqw' }} onClick = { e => e.stopPropagation() } data-testid = "rm-dialpad">
                <div className = "row" style = {{ justifyContent: 'space-between', marginBottom: '0.6cqw' }}>
                    <span className = "modal__title" style = {{ margin: 0 }}>Dial pad</span>
                    <span className = "linkbtn" onClick = { props.onClose }><X /></span>
                </div>
                <p className = "modal__body" style = {{ margin: '0 0 1.2cqw' }}>Send tones into the call (e.g. an IVR or PIN).</p>
                <div className = "dialpad__readout" data-testid = "rm-dialpad-readout">{ entered || '—' }</div>
                <div className = "dialpad">
                    { keys.map(k => (
                        <button key = { k } className = "dialkey" onClick = { () => press(k) } data-testid = { `rm-dialkey-${k}` }>{ k }</button>
                    )) }
                </div>
            </div>
        </div>
    );
}

/**
 * Chat panel — the Remote's window into meeting chat. It (a) shows the recent thread
 * the TV relays, so the controller can READ the conversation; (b) lets the controller
 * compose + send a message into the meeting (TV `sendChatMessage`); and (c) carries
 * an explicit toggle to OPEN/CLOSE the room's chat panel on the TV, independent of
 * sending. Stays open after a send (input cleared) so a second message needs no reopen.
 */
function ChatComposer(props: {
    onClose: () => void;
    onSend: (text: string) => void;
    chatPanelOpen: boolean;
    onSetChatOpen: (open: boolean) => void;
}): JSX.Element {
    const [text, setText] = useState<string>('');
    const [sent, setSent] = useState<number>(0);

    const send = (): void => {
        const trimmed = text.trim();

        if (!trimmed) {
            return;
        }
        props.onSend(trimmed);
        setText('');
        setSent(n => n + 1);
    };

    return (
        <div className = "scrim scrim--top" onClick = { props.onClose }>
            <div className = "modal" style = {{ maxWidth: '52cqw', textAlign: 'left' }} onClick = { e => e.stopPropagation() } data-testid = "rm-chat">
                <div className = "row" style = {{ justifyContent: 'space-between', marginBottom: '1.2cqw' }}>
                    <span className = "modal__title" style = {{ margin: 0 }}>Chat</span>
                    <div className = "row" style = {{ gap: '0.8cqw' }}>
                        <button
                            className = { `seg__opt${props.chatPanelOpen ? ' seg__opt--on' : ''}` }
                            onClick = { () => props.onSetChatOpen(!props.chatPanelOpen) }
                            data-testid = "rm-chat-tv-toggle">
                            <Monitor /> { props.chatPanelOpen ? 'Hide on TV' : 'Show on TV' }
                        </button>
                        <span className = "linkbtn" onClick = { props.onClose }><X /></span>
                    </div>
                </div>
                <div className = "ack" data-testid = "rm-chat-note" style = {{ margin: '0 0 1.2cqw' }}>
                    Messages you send appear on the room TV. A full chat view on the remote is coming later.
                </div>
                <textarea
                    className = "chat-input"
                    value = { text }
                    placeholder = "Message the meeting…"
                    rows = { 4 }
                    autoFocus = { true }
                    onChange = { e => setText(e.target.value) }
                    onKeyDown = { e => {
                        // Enter inserts a newline (multi-line composer); ⌘/Ctrl+Enter sends.
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && text.trim()) {
                            e.preventDefault();
                            send();
                        }
                    } }
                    data-testid = "rm-chat-input" />
                <div className = "modal__actions" style = {{ marginTop: '1.6cqw' }}>
                    <button className = "btn" disabled = { !text.trim() } onClick = { send } data-testid = "rm-chat-send">
                        <Chat /> Send
                    </button>
                </div>
                { sent > 0 ? (
                    <div className = "ack" style = {{ marginTop: '1cqw' }} data-testid = "rm-chat-sent">
                        Sent { sent } message{ sent === 1 ? '' : 's' } to the meeting
                    </div>
                ) : null }
            </div>
        </div>
    );
}

/** One labelled identifier row in the Meeting info panel, with an optional copy button. */
function InfoRow(props: { label: string; value: string; onCopy?: () => void; copied?: boolean }): JSX.Element {
    return (
        <div className = "inforow">
            <span className = "inforow__label">{ props.label }</span>
            <span className = "inforow__value">{ props.value }</span>
            { props.onCopy ? (
                <button className = "linkbtn inforow__copy" onClick = { props.onCopy }>
                    { props.copied ? <Check /> : 'Copy' }
                </button>
            ) : null }
        </div>
    );
}

/**
 * Meeting info — a read-only panel of identifiers the Remote/TV already hold:
 * room + meeting name, the join link (config meeting domain + meeting name) and the
 * share key for picking up another remote. Dial-in/PIN is not carried client-side in
 * this build, so it is shown as an honest gap rather than invented.
 */
function MeetingInfoModal(props: {
    roomName: string;
    meetingName: string;
    meetingDomain: string;
    shareHost: string;
    shareKey: string;
    onClose: () => void;
}): JSX.Element {
    const [copied, setCopied] = useState<string>('');
    const joinUrl = `https://${props.meetingDomain}/${props.meetingName}`;
    const shareUrl = props.shareKey ? `${props.shareHost}/${props.shareKey}` : '';

    const copy = (key: string, value: string): void => {
        try {
            void navigator.clipboard?.writeText(value);
        } catch {
            /* clipboard unavailable — the value is still shown for manual copy */
        }
        setCopied(key);
    };

    return (
        <div className = "scrim" onClick = { props.onClose }>
            <div className = "modal" style = {{ maxWidth: '46cqw', textAlign: 'left' }} onClick = { e => e.stopPropagation() } data-testid = "rm-meeting-info">
                <div className = "row" style = {{ justifyContent: 'space-between', marginBottom: '1.2cqw' }}>
                    <span className = "modal__title" style = {{ margin: 0 }}>Meeting info</span>
                    <span className = "linkbtn" onClick = { props.onClose }><X /></span>
                </div>
                <InfoRow label = "Room" value = { props.roomName } />
                <InfoRow label = "Meeting" value = { props.meetingName } />
                <InfoRow label = "Join link" value = { joinUrl } onCopy = { () => copy('join', joinUrl) } copied = { copied === 'join' } />
                { shareUrl ? (
                    <InfoRow label = "Share key" value = { shareUrl } onCopy = { () => copy('share', props.shareKey) } copied = { copied === 'share' } />
                ) : null }
                <InfoRow label = "Dial-in" value = "Not configured for this room" />
            </div>
        </div>
    );
}

/** Pick a meeting participant to pin to the TV's main stage (or unpin everyone). */
function PinPicker(props: {
    roster: MeetingParticipant[];
    pinnedId: string;
    onPin: (participantId: string) => void;
    onClose: () => void;
}): JSX.Element {
    const pick = (id: string): void => {
        props.onPin(id);
        props.onClose();
    };

    return (
        <div className = "scrim" onClick = { props.onClose }>
            <div className = "modal" style = {{ maxWidth: '40cqw', textAlign: 'left' }} onClick = { e => e.stopPropagation() } data-testid = "rm-pin-picker">
                <div className = "row" style = {{ justifyContent: 'space-between', marginBottom: '1.2cqw' }}>
                    <span className = "modal__title" style = {{ margin: 0 }}>Pin to stage</span>
                    <span className = "linkbtn" onClick = { props.onClose }><X /></span>
                </div>
                <div className = "pinpick">
                    { props.pinnedId ? (
                        <button className = "pinpick__row" onClick = { () => pick('') } data-testid = "rm-pin-unpin">
                            <span className = "pinpick__name">Unpin everyone</span>
                        </button>
                    ) : null }
                    { props.roster.length === 0 ? (
                        <div className = "pinpick__empty">No participants to pin yet.</div>
                    ) : props.roster.map(p => {
                        const name = (p.displayName ?? '').trim() || (p.local ? 'Room TV' : 'Guest');
                        const sub = p.local ? 'Room TV' : p.role === 'moderator' ? 'Host' : 'Participant';

                        return (
                            <button
                                key = { p.participantId }
                                className = { `pinpick__row${props.pinnedId === p.participantId ? ' pinpick__row--on' : ''}` }
                                onClick = { () => pick(p.participantId) }
                                data-testid = "rm-pin-row">
                                <Avatar label = { name } seed = { p.participantId } />
                                <div style = {{ minWidth: 0 }}>
                                    <div className = "pinpick__name">{ name }</div>
                                    <div className = "pinpick__sub">{ sub }</div>
                                </div>
                            </button>
                        );
                    }) }
                </div>
            </div>
        </div>
    );
}

/** Password prompt for a locked meeting (Jitsi `passwordRequired` on the TV). */
function PasswordModal(props: { onSubmit: (pw: string) => void }): JSX.Element {
    const [pw, setPw] = useState<string>('');

    return (
        <div className = "scrim">
            <div className = "modal" onClick = { e => e.stopPropagation() } data-testid = "rm-password">
                <div className = "modal__icon" style = {{ background: 'rgba(70, 135, 237, 0.16)', color: 'var(--blue-lt)' }}><Lock /></div>
                <h2 className = "modal__title">This meeting is locked</h2>
                <p className = "modal__body">Enter the meeting password so the room can join.</p>
                <input
                    className = "text-input"
                    type = "password"
                    value = { pw }
                    placeholder = "Password"
                    onChange = { e => setPw(e.target.value) }
                    onKeyDown = { e => { if (e.key === 'Enter' && pw) props.onSubmit(pw); } }
                    data-testid = "rm-password-input" />
                <div className = "modal__actions" style = {{ marginTop: '1.6cqw' }}>
                    <button className = "btn" disabled = { !pw } onClick = { () => props.onSubmit(pw) } data-testid = "rm-password-submit">
                        Submit password
                    </button>
                </div>
            </div>
        </div>
    );
}

function ConsentModal(props: { onCancel: () => void; onConfirm: () => void }): JSX.Element {
    const [informed, setInformed] = useState<boolean>(true);

    return (
        <div className = "scrim" onClick = { props.onCancel }>
            <div className = "modal" onClick = { e => e.stopPropagation() }>
                <div className = "modal__icon"><Record /></div>
                <h2 className = "modal__title">Start recording this meeting?</h2>
                <p className = "modal__body">
                    Everyone in the room and on the call will be notified. The recording captures audio, video,
                    and anything shared on screen, and is saved to the meeting organizer.
                </p>
                <div className = "checkrow" onClick = { () => setInformed(v => !v) }>
                    <span className = { `checkrow__box${informed ? '' : ' checkrow__box--off'}` } style = {{ fontSize: '1.6cqw' }}>
                        { informed ? <Check /> : null }
                    </span>
                    Participants have been informed
                </div>
                <div className = "modal__actions">
                    <button className = "btn btn--ghost" onClick = { props.onCancel }>Cancel</button>
                    <button
                        className = "btn btn--danger"
                        disabled = { !informed }
                        onClick = { props.onConfirm }
                        data-testid = "rm-record-confirm">
                        <Record /> Start recording
                    </button>
                </div>
            </div>
        </div>
    );
}

export function RemoteInCall(props: RemoteInCallProps): JSX.Element {
    const [sheet, setSheet] = useState<boolean>(false);
    const [consent, setConsent] = useState<boolean>(false);
    const [dialpad, setDialpad] = useState<boolean>(false);
    const [info, setInfo] = useState<boolean>(false);
    const [pinPicker, setPinPicker] = useState<boolean>(false);
    const [chat, setChat] = useState<boolean>(false);
    // Optimistic latches: the TV emits no echo event for captions, lobby or pin, so the
    // Remote tracks the toggle locally (same pattern as the whiteboard latch).
    const [captionsOn, setCaptionsOn] = useState<boolean>(false);
    const [lobbyOn, setLobbyOn] = useState<boolean>(false);
    const [pinnedId, setPinnedId] = useState<string>('');

    const tileOn = props.tileView;
    const handRaised = props.handRaised;

    const openChat = (): void => {
        setChat(true);
    };

    const toggleCaptions = (): void => {
        const next = !captionsOn;

        setCaptionsOn(next);
        props.onCaptions(next);
    };

    // Pin a participant (or unpin). Speaker/Tile are mutually exclusive with Pin in the
    // segmented control, so choosing a layout also clears any pin.
    const pin = (participantId: string): void => {
        setPinnedId(participantId);
        props.onPin(participantId);
    };

    const chooseLayout = (tile: boolean): void => {
        if (pinnedId) {
            pin('');
        }
        props.onLayout(tile);
    };

    // The TV always populates `participants` (at least itself) once joined; show a
    // single TV row as a graceful fallback until that first broadcast arrives.
    const roster: MeetingParticipant[] = props.participants.length
        ? props.participants
        : [{ participantId: 'tv', displayName: props.roomName, local: true }];

    const onRecordClick = (): void => {
        if (props.recording) {
            props.onRecord(false);
        } else {
            setConsent(true);
        }
    };

    return (
        <div className = "incall" data-testid = "rm-toolbar">
            <div className = "incall__top">
                <Dot kind = { props.tvConnected ? 'live' : 'idle' } />
                <span className = "incall__title">{ props.roomName }</span>
                <span className = "incall__sub">· { props.meetingName }</span>
                <span className = "spacer" />
                { props.recording ? <Pill label = "REC" variant = "rec" dot = { true } testId = "rm-rec-pill" /> : null }
                { props.screensharing ? <Pill label = "Sharing" variant = "on" testId = "rm-sharing-pill" /> : null }
                { handRaised ? <Pill label = "Hand up" variant = "on" /> : null }
                { props.isModerator ? <Pill label = "HOST" variant = "host" testId = "rm-moderator" /> : null }
            </div>

            <div className = "incall__controlling">
                <Monitor /> Controlling <b>{ props.roomName }</b> — changes appear on the room TV.
            </div>

            <div className = "incall__body">
                <div className = "incall__main">
                    <div className = "featrow">
                        <button
                            className = { `feat${handRaised ? ' feat--on' : ''}` }
                            onClick = { () => props.onRaiseHand(!handRaised) }
                            data-testid = "rm-raise">
                            <Hand /> { handRaised ? 'Lower' : 'Raise' }
                        </button>
                        <button
                            className = "feat"
                            onClick = { props.onWhiteboard }
                            data-testid = "rm-board">
                            <Board /> Board
                        </button>
                        <button
                            className = { `feat${props.screensharing ? ' feat--on' : ''}` }
                            onClick = { props.onShare }
                            data-testid = "rm-share-feat">
                            <ScreenShare /> Share
                        </button>
                        <button
                            className = { `feat${props.chatPanelOpen ? ' feat--on' : ''}` }
                            onClick = { openChat }
                            data-testid = "rm-chat-feat">
                            <Chat /> Chat
                        </button>
                    </div>

                    <div>
                        <div className = "group__label">Layout</div>
                        <div className = "seg" data-testid = "rm-layout">
                            <button
                                className = { `seg__opt${!tileOn && !pinnedId ? ' seg__opt--on' : ''}` }
                                onClick = { () => chooseLayout(false) }
                                data-testid = "rm-speaker">
                                <SpeakerView /> Speaker
                            </button>
                            <button
                                className = { `seg__opt${tileOn && !pinnedId ? ' seg__opt--on' : ''}` }
                                onClick = { () => chooseLayout(true) }
                                data-testid = "rm-tile">
                                <Tile /> Tile
                            </button>
                            <button
                                className = { `seg__opt${pinnedId ? ' seg__opt--on' : ''}` }
                                onClick = { () => setPinPicker(true) }
                                data-testid = "rm-pin">
                                <Pin /> Pin
                            </button>
                        </div>
                    </div>

                    { props.isModerator && (
                        <div className = "hostbox" data-testid = "rm-host-controls">
                            <div className = "group__label group__label--host">Host controls</div>
                            <div className = "hostrow">
                                <button
                                    className = { `seg__opt${props.recording ? ' seg__opt--on' : ''}` }
                                    style = { props.recording ? { background: 'var(--rec)', color: '#fff' } : undefined }
                                    onClick = { onRecordClick }
                                    data-testid = "rm-record">
                                    <Record /> { props.recording ? 'Recording' : 'Record' }
                                </button>
                                <button
                                    className = "seg__opt"
                                    onClick = { props.onMuteAll }
                                    data-testid = "rm-mute-all">
                                    <MicOff /> Mute all
                                </button>
                                <button
                                    className = { `seg__opt${lobbyOn ? ' seg__opt--on' : ''}` }
                                    onClick = { () => { const next = !lobbyOn; setLobbyOn(next); props.onLobby(next); } }
                                    data-testid = "rm-lobby">
                                    <Lock /> Lobby{ lobbyOn ? ' · On' : '' }
                                </button>
                            </div>
                        </div>
                    ) }

                    <div className = "spacer" />
                    <div className = "ack" data-testid = "rm-last-ack">{ props.lastAck || 'No command sent yet' }</div>
                </div>

                <div className = "roster" data-testid = "rm-roster">
                    <div className = "roster__head">
                        <span className = "roster__title">In the meeting <span>{ roster.length }</span></span>
                        <Users />
                    </div>
                    <div className = "roster__list">
                        { roster.map(p => <ParticipantRow key = { p.participantId } p = { p } />) }
                    </div>
                </div>
            </div>

            <div className = "primebar">
                <button className = { `prime${props.audioMuted ? ' prime--active' : ''}` } onClick = { props.onMute } data-testid = "rm-mute">
                    <span className = "prime__btn">{ props.audioMuted ? <MicOff /> : <Mic /> }</span>
                    <span className = "prime__label">{ props.audioMuted ? 'Unmute' : 'Mute' }</span>
                </button>
                <button className = { `prime${props.videoMuted ? ' prime--active' : ''}` } onClick = { props.onVideo } data-testid = "rm-video">
                    <span className = "prime__btn">{ props.videoMuted ? <VideoOff /> : <Video /> }</span>
                    <span className = "prime__label">{ props.videoMuted ? 'Start' : 'Stop' }</span>
                </button>
                <button className = { `prime${props.screensharing ? ' prime--active' : ''}` } onClick = { props.onShare } data-testid = "rm-share">
                    <span className = "prime__btn"><ScreenShare /></span>
                    <span className = "prime__label">{ props.screensharing ? 'Stop' : 'Share' }</span>
                </button>
                <button className = "prime" onClick = { () => setSheet(true) } data-testid = "rm-more">
                    <span className = "prime__btn"><More /></span>
                    <span className = "prime__label">More</span>
                </button>
                <button className = "prime prime--leave" onClick = { props.onHangup } data-testid = "rm-hangup">
                    <span className = "prime__btn"><Hangup /></span>
                    <span className = "prime__label">Leave</span>
                </button>
            </div>

            { sheet ? (
                <MoreSheet
                    onClose = { () => setSheet(false) }
                    onShare = { props.onShare }
                    onWhiteboard = { props.onWhiteboard }
                    onRaiseHand = { () => props.onRaiseHand(!handRaised) }
                    onDialpad = { () => setDialpad(true) }
                    captionsOn = { captionsOn }
                    onCaptions = { toggleCaptions }
                    onMeetingInfo = { () => setInfo(true) } />
            ) : null }
            { dialpad ? (
                <DialPad onClose = { () => setDialpad(false) } onTone = { props.onTone } />
            ) : null }
            { chat ? (
                <ChatComposer
                    onClose = { () => setChat(false) }
                    onSend = { props.onChat }
                    chatPanelOpen = { props.chatPanelOpen }
                    onSetChatOpen = { props.onSetChatOpen } />
            ) : null }
            { info ? (
                <MeetingInfoModal
                    roomName = { props.roomName }
                    meetingName = { props.meetingName }
                    meetingDomain = { props.meetingDomain }
                    shareHost = { props.shareHost }
                    shareKey = { props.shareKey }
                    onClose = { () => setInfo(false) } />
            ) : null }
            { pinPicker ? (
                <PinPicker
                    roster = { roster }
                    pinnedId = { pinnedId }
                    onPin = { pin }
                    onClose = { () => setPinPicker(false) } />
            ) : null }
            { consent ? (
                <ConsentModal
                    onCancel = { () => setConsent(false) }
                    onConfirm = { () => { setConsent(false); props.onRecord(true); } } />
            ) : null }
            { props.needPassword ? (
                <PasswordModal onSubmit = { props.onSubmitPassword } />
            ) : null }
        </div>
    );
}
